import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { id: "studio",  label: "О нас",    section: "studio" },
  { id: "skills",  label: "Навыки",   section: "skills" },
  { id: "work",    label: "Работы",   section: "work" },
  { id: "method",  label: "Метод",    section: "method" },
  { id: "team",    label: "Команда",  section: "team" },
  { id: "clients", label: "Клиенты",  section: "clients" },
  { id: "contact", label: "Контакт",  section: null },  // footer, always visible
];

function IconVimeo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.404 0-2.59-1.297-3.553-3.892l-1.945-7.137c-.722-2.593-1.494-3.89-2.318-3.89-.18 0-.81.378-1.893 1.126L0 7.139c1.2-1.053 2.38-2.108 3.547-3.16C5.087 2.69 6.24 1.93 7.005 1.86c1.81-.173 2.93 1.07 3.354 3.728.452 2.872.766 4.655.942 5.347.522 2.4 1.097 3.6 1.726 3.6.486 0 1.215-.77 2.187-2.312.969-1.541 1.485-2.713 1.548-3.515.123-1.182-.342-1.776-1.395-1.776-.495 0-1.005.115-1.531.341 1.022-3.355 2.972-4.985 5.852-4.892 2.135.063 3.14 1.45 3.014 4.135z"/>
    </svg>
  );
}
function IconBehance() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M22 7h-7v-2h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14h-8.027c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.965c-.105-1.547-1.136-2.219-2.477-2.219-1.466 0-2.277.768-2.488 2.219zm-9.574 6.988h-6.466v-14.967h6.953c5.476.081 5.58 5.444 2.72 6.906 3.461 1.26 3.577 8.061-3.207 8.061zm-3.466-8.988h3.584c2.508 0 2.906-3-.312-3h-3.272v3zm3.391 3h-3.391v3.016h3.341c3.055 0 2.868-3.016.05-3.016z"/>
    </svg>
  );
}
function IconTelegram() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M22.05 2.55 1.86 10.45c-1.18.46-1.17 1.13-.22 1.42l5.18 1.62 11.99-7.57c.57-.34 1.09-.16.66.22L9.78 14.94l-.36 5.32c.47 0 .67-.21.93-.46l2.23-2.17 4.63 3.42c.85.47 1.46.23 1.67-.79l3.03-14.27c.31-1.25-.49-1.82-1.86-1.44z"/>
    </svg>
  );
}

const SOCIALS = [
  { key: "vimeo",    Icon: IconVimeo,    label: "Vimeo",    field: "social_vimeo" },
  { key: "behance",  Icon: IconBehance,  label: "Behance",  field: "social_behance" },
  { key: "telegram", Icon: IconTelegram, label: "Telegram", field: "social_telegram" },
];

export default function Navigation({ site = {}, onDiscussClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    // If we're not on the landing page, navigate home first (anchor will be honoured)
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setOpen(false);
  };

  const goHome = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const enabled = site.section_enabled || {};
  const links = NAV_LINKS.filter((l) => l.section === null || enabled[l.section] !== false);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-4">
        <button
          data-testid="nav-logo"
          onClick={goHome}
          className="flex items-center gap-3 group"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-[#a78bfa] bbf-pulse" />
          <span className="font-display text-[13px] tracking-[0.18em] uppercase">Big&nbsp;Bag&nbsp;Films</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goTo(l.id)}
              className="bbf-link text-[12px] tracking-[0.22em] uppercase text-white/80 hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-3 mr-1">
            {SOCIALS.map(({ key, Icon, label, field }) => (
              <a
                key={key}
                data-testid={`nav-social-${key}`}
                href={site[field] || "#"}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="text-white/55 hover:text-white transition-colors"
              >
                <Icon />
              </a>
            ))}
          </div>
          <button
            data-testid="nav-discuss-button"
            onClick={onDiscussClick}
            className="bbf-btn-primary px-5 py-2.5 uppercase text-[11px] tracking-[0.22em] font-bold"
          >
            {site.discuss_button || "Обсудить проект"}
          </button>
        </div>

        <button
          data-testid="nav-burger"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden flex flex-col gap-[5px] p-2"
          aria-label="Toggle menu"
        >
          <span className={`block h-[1.5px] w-6 bg-white transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
          <span className={`block h-[1.5px] w-6 bg-white transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`block h-[1.5px] w-6 bg-white transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
        </button>
      </div>

      <div
        data-testid="nav-mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-black/95 backdrop-blur-xl ${
          open ? "max-h-[640px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-5 border-t border-white/10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => goTo(l.id)}
              data-testid={`nav-mobile-${l.id}`}
              className="text-left font-display text-2xl uppercase tracking-tight"
            >
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-5 mt-2">
            {SOCIALS.map(({ key, Icon, label, field }) => (
              <a
                key={key}
                href={site[field] || "#"}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="text-white/65 hover:text-white"
              >
                <Icon />
              </a>
            ))}
          </div>
          <button
            onClick={() => { setOpen(false); onDiscussClick && onDiscussClick(); }}
            data-testid="nav-mobile-discuss"
            className="mt-3 self-start bbf-btn-primary px-6 py-3 uppercase text-[11px] tracking-[0.22em] font-bold"
          >
            {site.discuss_button || "Обсудить проект"}
          </button>
        </div>
      </div>
    </header>
  );
}
